import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { http } from "@/lib/api";
import { ShieldCheck, ArrowRight, Sparkle } from "@phosphor-icons/react";

/**
 * YouthPrivacyPolicy — the canonical policy page that every SocialPulse
 * site's YouthID disclaimer links to.
 *
 * Registered on multiple URLs so the same page serves NP / FP / AP / IA:
 *   /about/youth-privacy       (NaturePulse / FarmersPulse)
 *   /policies/student-privacy  (AlumniPulse)
 *   /policies/dpdp-2023        (IndiaAlumni)
 *
 * The page hydrates the site's disclaimer pack at render time so each
 * brand's citations and copy stay in sync with the API.
 */
export default function YouthPrivacyPolicy() {
  const [packs, setPacks] = useState(null);

  useEffect(() => {
    http.get("/youth/disclaimer-packs/all")
      .then((r) => setPacks(r.data.disclaimers))
      .catch(() => setPacks({}));
  }, []);

  const siteId = (() => {
    if (typeof process !== "undefined" && process.env.REACT_APP_SITE_ID) {
      return process.env.REACT_APP_SITE_ID;
    }
    const host = typeof window !== "undefined" ? window.location.hostname : "";
    if (/farmerspulse/i.test(host)) return "farmerspulse";
    if (/indiaalumni/i.test(host))  return "indiaalumni";
    if (/alumnipulse/i.test(host))  return "alumnipulse";
    return "naturepulse";
  })();

  const primary = packs?.[siteId];

  return (
    <main
      className="min-h-screen bg-[color:var(--np-bg,#F5EBDC)] pt-36 md:pt-40 pb-20 px-5"
      data-testid="youth-privacy-policy"
    >
      <div className="max-w-3xl mx-auto">
        <div className="font-mono-tech text-[10px] tracking-[0.32em] uppercase text-[color:var(--np-brand,#B85F2A)]">
          <Sparkle size={12} weight="fill" className="inline mr-1" />
          SocialPulse Youth Privacy
        </div>
        <h1 className="mt-1 font-serif-display text-4xl md:text-5xl leading-[1.02] tracking-tight">
          Every child on our network is protected by five layers.
        </h1>
        <p className="mt-3 font-serif-display italic text-lg text-[#3A3830] max-w-2xl">
          A single, cross-site policy that governs how SocialPulse mints,
          renders and retires identities for minors. Owned by NaturePulse;
          consumed by every SocialPulse property.
        </p>

        <section className="mt-10 grid gap-3">
          {[
            ["No full name", "Only a first name or chosen alias is ever public."],
            ["No exact age", "Only an age band (e.g. Age Group 11–13) is public."],
            ["No precise location", "Coordinates are fuzzed to a school- or region-level cell."],
            ["No EXIF / GPS", "Every image is re-encoded server-side; the original stays private."],
            ["No adult DMs", "Adults comment on observations, never on a Youth Code."],
          ].map(([title, body]) => (
            <div
              key={title}
              className="rounded-3xl border border-black/10 bg-white p-5"
              data-testid={`policy-rule-${title.toLowerCase().replace(/\s+/g, "-")}`}
            >
              <div className="font-mono-tech text-[10px] tracking-[0.32em] uppercase text-[color:var(--np-brand,#B85F2A)]">
                <ShieldCheck size={11} weight="fill" className="inline mr-1" /> Rule
              </div>
              <h3 className="mt-1 font-serif-display text-xl">{title}</h3>
              <p className="mt-1 text-[#3A3830]">{body}</p>
            </div>
          ))}
        </section>

        {primary && (
          <section
            className="mt-10 rounded-3xl bg-[#1B4332] text-[#F5EBDC] p-6"
            data-testid="policy-site-pack"
          >
            <div className="font-mono-tech text-[10px] tracking-[0.32em] uppercase text-[color:var(--np-brand,#B85F2A)]">
              {primary.eyebrow}
            </div>
            <h3 className="mt-1 font-serif-display text-2xl">{primary.title}</h3>
            <p className="mt-2 max-w-xl text-[#E7DDCA]">{primary.body}</p>
            {primary.citations?.length > 0 && (
              <div className="mt-3 flex flex-wrap gap-1.5">
                {primary.citations.map((c) => (
                  <span
                    key={c}
                    className="rounded-full bg-white/10 text-[#E7DDCA] px-2 py-0.5 font-mono-tech text-[9px] tracking-[0.24em] uppercase"
                  >
                    {c}
                  </span>
                ))}
              </div>
            )}
            <div className="mt-3 font-mono-tech text-[9px] tracking-[0.24em] uppercase text-[#E7DDCA]/70">
              {primary.brand} · Youth Privacy Pack
            </div>
          </section>
        )}

        {packs && Object.keys(packs).length > 1 && (
          <section className="mt-10">
            <div className="font-mono-tech text-[10px] tracking-[0.32em] uppercase text-[color:var(--np-brand,#B85F2A)]">
              Also enforced on
            </div>
            <h3 className="mt-1 font-serif-display text-2xl">Every SocialPulse site.</h3>
            <div className="mt-3 grid sm:grid-cols-2 gap-3">
              {Object.entries(packs)
                .filter(([id]) => id !== siteId)
                .map(([id, p]) => (
                  <div
                    key={id}
                    className="rounded-3xl border border-black/10 bg-white p-4"
                    data-testid={`policy-partner-${id}`}
                  >
                    <div className="font-mono-tech text-[10px] tracking-[0.32em] uppercase text-[color:var(--np-brand,#B85F2A)]">
                      {p.brand}
                    </div>
                    <div className="mt-1 font-serif-display text-lg">{p.title}</div>
                    <p className="mt-1 text-sm text-[#3A3830]">{p.body}</p>
                  </div>
                ))}
            </div>
          </section>
        )}

        <div className="mt-12">
          <Link
            to="/"
            data-testid="policy-back-home"
            className="inline-flex items-center gap-1 font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[color:var(--np-brand,#B85F2A)]"
          >
            Back to home <ArrowRight size={10} weight="bold" />
          </Link>
        </div>
      </div>
    </main>
  );
}
